import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Nullable } from 'corgi-types';
import debounce from 'lodash-es/debounce';

type ListRef = RefObject<Nullable<HTMLDivElement>>;

// Debounce timeout on scroll/resize events
const DEBOUNCE_WAIT = 50;

// Width subtracted when navigating - in order to show last visible item after navigation
const SCROLLING_MARGIN = 200;

// Render Next control only when content is longer than visible width at least by:
const MIN_SCROLL_RIGHT_MARGIN = 30;

// Scroll behaviour
const SCROLL_BEHAVIOUR = 'smooth';

const useControls = (listRef: ListRef) => {
  const [hasPrevControl, setHasPrevControl] = useState(false);
  const [hasNextControl, setHasNextControl] = useState(false);

  const updateControls = debounce(() => {
    if (!listRef.current) {
      return;
    }
    const { scrollLeft, scrollWidth, clientWidth } = listRef.current;

    // Condition to render left arrow
    setHasPrevControl(scrollLeft > 0);

    // Condition to render right arrow
    setHasNextControl(scrollLeft + MIN_SCROLL_RIGHT_MARGIN < scrollWidth - clientWidth);
  }, DEBOUNCE_WAIT);

  return { hasPrevControl, hasNextControl, updateControls };
};

const getPreviousSlideHandler = (listRef: ListRef) => () => {
  if (!listRef.current) {
    return;
  }

  const updatedScroll = listRef.current.scrollLeft - listRef.current.clientWidth + SCROLLING_MARGIN;

  listRef.current.scrollTo({
    left: updatedScroll > 0 ? updatedScroll : 0,
    behavior: SCROLL_BEHAVIOUR,
  });
};

const getNextSlideHandler = (listRef: ListRef) => () => {
  if (!listRef.current) {
    return;
  }

  const updatedScroll = listRef.current.scrollLeft + listRef.current.clientWidth - SCROLLING_MARGIN;

  listRef.current.scrollTo({
    left:
      updatedScroll <= listRef.current.scrollWidth ? updatedScroll : listRef.current.scrollWidth,
    behavior: SCROLL_BEHAVIOUR,
  });
};

export const useCarouselNavigation = () => {
  const listRef = useRef<HTMLDivElement>(null);
  const { hasPrevControl, hasNextControl, updateControls } = useControls(listRef);

  useEffect(() => {
    updateControls();

    window.addEventListener('resize', updateControls);

    return () => window.removeEventListener('resize', updateControls);
  }, []);

  return {
    listRef,
    hasPrevControl,
    hasNextControl,
    updateControls,
    onPreviousSlide: getPreviousSlideHandler(listRef),
    onNextSlide: getNextSlideHandler(listRef),
  };
};
